import * as THREE from 'three';

export interface LevelMeta {
  name: string;
  x0: number;
  z0: number;
  size: number;
  res: number;
  hmin: number;
  hmax: number;
  heights: string;
  ao: string;
  albedo: string;
}

interface Pyramid {
  res: number;
  min: Float32Array;
  max: Float32Array;
}

export interface TerrainLevel extends LevelMeta {
  ps: number;
  data: Float32Array;
  pyramid: Pyramid[];
  block: number;
  heightTex: THREE.DataTexture;
  normalTex: THREE.DataTexture;
  albedoTex: THREE.Texture;
  /** blend margin (m) toward the next coarser level */
  margin: number;
}

const MARGINS: Record<string, number> = { near: 40, mid: 360, far: 0 };

/**
 * Multi-resolution elevation model: near (1 m lidar), mid (6 m), far (128 m), with seamless blending.
 * CPU queries mirror the GPU sampling in the terrain shader exactly.
 */
export class TerrainData {
  levels: TerrainLevel[] = [];
  readonly base: string;

  constructor(base: string) {
    this.base = base;
  }

  async load(onProgress: (frac: number, label: string) => void, maxAniso: number) {
    const meta = await (await fetch(`${this.base}terrain.json`)).json();
    const metas: LevelMeta[] = meta.levels;
    const worker = new Worker(new URL('./terrain.worker.ts', import.meta.url), { type: 'module' });
    let done = 0;
    const total = metas.length * 2;
    const step = (label: string) => onProgress(++done / total, label);
    const decode = (m: LevelMeta, id: number) =>
      new Promise<{ heights: Float32Array; normals: Uint8Array; pyramid: Pyramid[]; block: number }>((resolve, reject) => {
        const onMsg = (ev: MessageEvent) => {
          if (ev.data.id !== id) return;
          worker.removeEventListener('message', onMsg);
          if (ev.data.ok) resolve(ev.data);
          else reject(new Error(ev.data.error));
        };
        worker.addEventListener('message', onMsg);
        worker.postMessage({
          id,
          url: new URL(`${this.base}${m.heights}`, location.href).href,
          aoUrl: new URL(`${this.base}${m.ao}`, location.href).href,
          res: m.res,
          hmin: m.hmin,
          hmax: m.hmax,
          ps: m.size / m.res,
        });
      });
    const loader = new THREE.TextureLoader();
    const loadTex = (url: string, srgb: boolean) =>
      new Promise<THREE.Texture>((resolve, reject) => {
        loader.load(`${this.base}${url}`, (t) => {
          t.flipY = false;
          t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
          t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
          t.minFilter = THREE.LinearMipmapLinearFilter;
          t.magFilter = THREE.LinearFilter;
          t.anisotropy = maxAniso;
          t.generateMipmaps = true;
          t.needsUpdate = true;
          resolve(t);
        }, undefined, reject);
      });

    const results = await Promise.all(metas.map(async (m, i) => {
      const [dec, albedo] = await Promise.all([
        decode(m, i).then((r) => { step(`${m.name} elevation`); return r; }),
        loadTex(m.albedo, true).then((t) => { step(`${m.name} imagery`); return t; }),
      ]);
      return { m, dec, albedo };
    }));
    worker.terminate();

    for (const { m, dec, albedo } of results) {
      const heightTex = new THREE.DataTexture(dec.heights, m.res, m.res, THREE.RedFormat, THREE.FloatType);
      heightTex.minFilter = heightTex.magFilter = THREE.NearestFilter;
      heightTex.wrapS = heightTex.wrapT = THREE.ClampToEdgeWrapping;
      heightTex.needsUpdate = true;
      // RGBA: normal.x, normal.z, sky visibility, vegetation density
      const normalTex = new THREE.DataTexture(dec.normals, m.res, m.res, THREE.RGBAFormat, THREE.UnsignedByteType);
      normalTex.minFilter = THREE.LinearMipmapLinearFilter;
      normalTex.magFilter = THREE.LinearFilter;
      normalTex.generateMipmaps = true;
      normalTex.anisotropy = maxAniso;
      normalTex.wrapS = normalTex.wrapT = THREE.ClampToEdgeWrapping;
      normalTex.needsUpdate = true;
      this.levels.push({
        ...m,
        ps: m.size / m.res,
        data: dec.heights,
        pyramid: dec.pyramid,
        block: dec.block,
        heightTex,
        normalTex,
        albedoTex: albedo,
        margin: MARGINS[m.name] ?? 0,
      });
    }
    // fine -> coarse ordering
    const order = ['near', 'mid', 'far'];
    this.levels.sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));
  }

  /** Bilinear sample matching texelFetch-based sampling in the shader. */
  private sampleLevel(L: TerrainLevel, x: number, z: number): number {
    const res = L.res;
    let fx = (x - L.x0) / L.ps - 0.5;
    let fz = (z - L.z0) / L.ps - 0.5;
    fx = Math.min(Math.max(fx, 0), res - 1.001);
    fz = Math.min(Math.max(fz, 0), res - 1.001);
    const ix = Math.floor(fx), iz = Math.floor(fz);
    const tx = fx - ix, tz = fz - iz;
    const d = L.data;
    const i0 = iz * res + ix;
    const h00 = d[i0], h10 = d[i0 + 1], h01 = d[i0 + res], h11 = d[i0 + res + 1];
    return (h00 * (1 - tx) + h10 * tx) * (1 - tz) + (h01 * (1 - tx) + h11 * tx) * tz;
  }

  /** Blend weight of a level (1 inside, fading to 0 at its border over `margin`). */
  edgeWeight(L: TerrainLevel, x: number, z: number): number {
    if (L.margin <= 0) return 1;
    const dx = Math.min(x - L.x0, L.x0 + L.size - x);
    const dz = Math.min(z - L.z0, L.z0 + L.size - z);
    const w = Math.min(Math.max(Math.min(dx, dz) / L.margin, 0), 1);
    return w * w * (3 - 2 * w);
  }

  heightAt(x: number, z: number): number {
    const lv = this.levels;
    let h = this.sampleLevel(lv[lv.length - 1], x, z);
    for (let i = lv.length - 2; i >= 0; i--) {
      const w = this.edgeWeight(lv[i], x, z);
      if (w > 0) h = h + (this.sampleLevel(lv[i], x, z) - h) * w;
    }
    return h;
  }

  normalAt(x: number, z: number, out = new THREE.Vector3(), e = 0.75): THREE.Vector3 {
    const hx = this.heightAt(x + e, z) - this.heightAt(x - e, z);
    const hz = this.heightAt(x, z + e) - this.heightAt(x, z - e);
    return out.set(-hx, 2 * e, -hz).normalize();
  }

  /** Conservative min/max height over an axis-aligned square region. */
  rangeAt(x0: number, z0: number, size: number): [number, number] {
    let mn = Infinity, mx = -Infinity;
    for (const L of this.levels) {
      if (x0 > L.x0 + L.size || x0 + size < L.x0 || z0 > L.z0 + L.size || z0 + size < L.z0) continue;
      // choose the pyramid level whose cell is >= size/4
      const cellBase = L.ps * L.block;
      let pl = 0;
      while (pl < L.pyramid.length - 1 && cellBase * (1 << pl) < size / 4) pl++;
      const P = L.pyramid[pl];
      const cell = cellBase * (1 << pl);
      const c0 = Math.max(0, Math.floor((x0 - L.x0) / cell));
      const c1 = Math.min(P.res - 1, Math.floor((x0 + size - L.x0) / cell));
      const r0 = Math.max(0, Math.floor((z0 - L.z0) / cell));
      const r1 = Math.min(P.res - 1, Math.floor((z0 + size - L.z0) / cell));
      for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) {
        const i = r * P.res + c;
        if (P.min[i] < mn) mn = P.min[i];
        if (P.max[i] > mx) mx = P.max[i];
      }
    }
    if (mn === Infinity) {
      // outside all data: clamp-to-edge of far level
      const h = this.heightAt(x0 + size / 2, z0 + size / 2);
      return [h - 50, h + 50];
    }
    return [mn, mx];
  }

  /** Bounds of the near (detailed) level. */
  get near(): TerrainLevel {
    return this.levels[0];
  }
}
